// Wire-format and tuning constants shared across modules.
//
// Audio: must match the server (`SEND_SAMPLE_RATE` / `RECEIVE_SAMPLE_RATE` in
// app/core/settings.py). The backend expects 16 kHz int16 mono input
// and emits 24 kHz int16 mono output.

export const SEND_RATE = 16000;
export const RECV_RATE = 24000;

// Default workflow id used when /workflows lists it; matches DEFAULT_WORKFLOW_ID
// in app/workflows/repository.py.
export const DEFAULT_WORKFLOW_ID = "google_pixel_order_packing";

// Continuous video frame policy.
//   - FRAME_INTERVAL_MS: minimum spacing between captured JPEG frames.
//   - FRAME_MAX_DIM: longest side of the captured frame in px.
//   - JPEG_QUALITY: 0..1 quality passed to canvas.toDataURL.
export const FRAME_INTERVAL_MS = 1000;
export const FRAME_MAX_DIM = 1024;
export const JPEG_QUALITY = 0.7;

// Observer composite video defaults. Composite mode still sends one ordinary
// image message per tick; these caps keep the merged frame under the backend
// MAX_IMAGE_BYTES limit while preserving both source views.
export const OBSERVER_COMPOSITE_MAX_WIDTH = 1280;
export const OBSERVER_COMPOSITE_MAX_HEIGHT = 1600;
export const OBSERVER_COMPOSITE_JPEG_QUALITY = 0.75;
export const OBSERVER_COMPOSITE_MAX_BYTES = 4 * 1024 * 1024;
export const OBSERVER_COMPOSITE_REQUIRED_WARNING_MS = 5000;

// Document scan capture policy. These are intentionally separate from the
// continuous-video constants above because the two flows have opposite
// trade-offs: continuous video is small + cheap because it streams at 1 fps
// to the long-running observer session; the scan still must be high-quality
// because the model has exactly ONE chance to OCR the document.
export const DOCUMENT_SCAN_SOURCE_INDEX = 2;
export const DOCUMENT_SCAN_RETRY_MS = 3500;
export const DOCUMENT_SCAN_MAX_DIM = 1920;
export const DOCUMENT_SCAN_JPEG_QUALITY = 0.92;
// Hard cap on the encoded JPEG size in bytes. The server independently caps
// at MAX_IMAGE_BYTES (4 MiB); we reject locally so we don't waste an
// upload.
export const DOCUMENT_SCAN_MAX_BYTES = 4 * 1024 * 1024;
// Local fallback compression ladder: if the first encode is over the byte
// cap we re-encode at progressively lower quality before retrying with a
// reduced max dimension.
export const DOCUMENT_SCAN_QUALITY_LADDER = [0.92, 0.85, 0.75];
export const DOCUMENT_SCAN_FALLBACK_MAX_DIM = 1280;

// Workflow auto-checking. The browser sends a "check" instruction on this
// interval whenever the model isn't already mid-turn.
export const AUTO_CHECK_INTERVAL_MS = 4000;
export const AUTO_START_ACTION = "begin";

// Continuous video sending. When false the browser only forwards frames during
// CHECK / NEXT / PREV / BEGIN turns; this keeps the cost of long observation
// sessions down. The server-side debug summary surfaces frame counts so users
// can sanity-check their bill.
export const SEND_CONTINUOUS_VIDEO_FRAMES = true;

// Backpressure. Browser WebSockets buffer outbound frames in a per-socket
// queue exposed via `bufferedAmount`. When that exceeds these thresholds we
// drop new frames to avoid unbounded memory growth on slow links.
export const WS_DROP_IMAGE_BUFFER_BYTES = 512 * 1024;
export const WS_DROP_AUDIO_BUFFER_BYTES = 1 * 1024 * 1024;

// Debug log capacity (in events). Events older than this are dropped.
export const DEBUG_EVENT_CAP = 1500;
